/**
 * Message keys (namespace 'expr'; English text in expr.messages.ts) and the internal failure type of the
 * expression language (W1-10c core-expr).
 *
 * Every error carries the character range [from, to) of the source text to underline (UTF-16 offsets into the
 * text the user typed). Parse-time errors come from the lexer, the Pratt parser and the checker (names, units,
 * argument counts); run-time errors from evaluation (division by zero, missing item values, functions outside
 * their domain).
 */

import type { ExprError } from './types';

export const EXPR_MSG = {
  // Lexer and parser
  empty: 'expr.empty',
  emptyAfterAssign: 'expr.emptyAfterAssign',
  unexpectedChar: 'expr.unexpectedChar',
  unexpectedToken: 'expr.unexpectedToken',
  unexpectedEnd: 'expr.unexpectedEnd',
  expectedValue: 'expr.expectedValue',
  missingOperator: 'expr.missingOperator',
  unitSpace: 'expr.unitSpace',
  unitSpaceOrMul: 'expr.unitSpaceOrMul',
  percentOrRemainder: 'expr.percentOrRemainder',
  percentSpace: 'expr.percentSpace',
  unclosedParen: 'expr.unclosedParen',
  unmatchedParen: 'expr.unmatchedParen',
  strayComma: 'expr.strayComma',
  assignNotFirst: 'expr.assignNotFirst',
  assignHere: 'expr.assignHere',
  unclosedString: 'expr.unclosedString',
  badDollar: 'expr.badDollar',
  badNumber: 'expr.badNumber',
  numberTooLarge: 'expr.numberTooLarge',
  tooDeep: 'expr.tooDeep',
  // Checker: names, functions, units
  unknownUnit: 'expr.unknownUnit',
  implicitMul: 'expr.implicitMul',
  speedUnitHere: 'expr.speedUnitHere',
  unknownName: 'expr.unknownName',
  unknownNameSuggest: 'expr.unknownNameSuggest',
  varNotHere: 'expr.varNotHere',
  notAFunction: 'expr.notAFunction',
  unknownFunction: 'expr.unknownFunction',
  unknownFunctionSuggest: 'expr.unknownFunctionSuggest',
  needsCall: 'expr.needsCall',
  argCount: 'expr.argCount',
  argCountRange: 'expr.argCountRange',
  argCountMin: 'expr.argCountMin',
  stringHere: 'expr.stringHere',
  unknownEasing: 'expr.unknownEasing',
  unknownEasingSuggest: 'expr.unknownEasingSuggest',
  easingId: 'expr.easingId',
  mixedUnits: 'expr.mixedUnits',
  mixedTime: 'expr.mixedTime',
  unitProduct: 'expr.unitProduct',
  unitQuotient: 'expr.unitQuotient',
  speedUnitOp: 'expr.speedUnitOp',
  scaleByUnit: 'expr.scaleByUnit',
  fieldWantsBeats: 'expr.fieldWantsBeats',
  fieldWantsSeconds: 'expr.fieldWantsSeconds',
  timeUnitUnknown: 'expr.timeUnitUnknown',
  fieldNotTime: 'expr.fieldNotTime',
  timeAsNumber: 'expr.timeAsNumber',
  beatArg: 'expr.beatArg',
  secArg: 'expr.secArg',
  // Evaluation
  divByZero: 'expr.divByZero',
  modByZero: 'expr.modByZero',
  undefinedFn: 'expr.undefinedFn',
  undefinedPow: 'expr.undefinedPow',
  tooLarge: 'expr.tooLarge',
  notFinite: 'expr.notFinite',
  missingValue: 'expr.missingValue',
  needsTempo: 'expr.needsTempo',
  needsSegment: 'expr.needsSegment',
  roundDigits: 'expr.roundDigits',
  easingRuntime: 'expr.easingRuntime',
} as const;

export type ExprMsgKey = (typeof EXPR_MSG)[keyof typeof EXPR_MSG];

/** Help texts for the reference (variables, constants, functions, units, assignments). */
export const EXPR_HELP = {
  'var.v': 'expr.var.v',
  'var.i': 'expr.var.i',
  'var.n': 'expr.var.n',
  'var.u': 'expr.var.u',
  'var.t': 'expr.var.t',
  'var.s': 'expr.var.s',
  'var.x': 'expr.var.x',
  'var.speed': 'expr.var.speed',
  'var.size': 'expr.var.size',
  'var.v0': 'expr.var.v0',
  'var.v1': 'expr.var.v1',
  'var.len': 'expr.var.len',
  'var.pathT': 'expr.var.pathT',
  'var.pathBeat': 'expr.var.pathBeat',
  'const.pi': 'expr.const.pi',
  'const.e': 'expr.const.e',
  'unit.px': 'expr.unit.px',
  'unit.pct': 'expr.unit.pct',
  'unit.deg': 'expr.unit.deg',
  'unit.s': 'expr.unit.s',
  'unit.ms': 'expr.unit.ms',
  'unit.b': 'expr.unit.b',
  'unit.v': 'expr.unit.v',
  'assign.set': 'expr.assign.set',
  'assign.add': 'expr.assign.add',
  'assign.sub': 'expr.assign.sub',
  'assign.mul': 'expr.assign.mul',
  'assign.div': 'expr.assign.div',
} as const;

export type ExprHelpKey = (typeof EXPR_HELP)[keyof typeof EXPR_HELP];

/** Thrown inside the lexer, parser, checker and evaluator; converted to `Result` at the API boundary. */
export class ExprFailure extends Error {
  readonly error: ExprError;

  constructor(error: ExprError) {
    super(error.key);
    this.name = 'ExprFailure';
    this.error = error;
  }
}

export interface Span {
  readonly from: number;
  readonly to: number;
}

export function exprError(key: string, span: Span, params?: Readonly<Record<string, string | number>>): ExprError {
  return params === undefined ? { key, from: span.from, to: span.to } : { key, params, from: span.from, to: span.to };
}

/** Throws an ExprFailure for `key` at `span`. */
export function fail(key: string, span: Span, params?: Readonly<Record<string, string | number>>): never {
  throw new ExprFailure(exprError(key, span, params));
}
